import React from 'react';
import { useTranslation } from './i18n.js';
import { openCard } from './collapsible.js';

// Sommaire de la page d'accueil, sorti du cadre Édito pour rester visible même quand ce cadre est
// replié : barre collée en haut de l'écran pendant le défilement (voir section-nav.css). L'ordre
// suit celui des sections dans public/js/app.js ; les libellés réutilisent les titres des cadres.
const NAV_ITEMS = [
  { id: 'edito', labelKey: 'cardLabelEdito' },
  { id: 'history', labelKey: 'editoTocHistory' },
  { id: 'distributors', labelKey: 'distributorsTitle' },
  { id: 'oma', labelKey: 'editoTocAuthor' },
  { id: 'collection', labelKey: 'collectionCardTitle' },
  { id: 'thought', labelKey: 'editoTocThought' },
];

export function SectionNav() {
  const { t } = useTranslation();
  const [activeId, setActiveId] = React.useState('');
  const navRef = React.useRef(null);
  const offsetRef = React.useRef(120);

  // Section courante : la dernière dont le haut est passé sous la barre. Recalculé au défilement
  // (léger : six éléments) plutôt qu'avec IntersectionObserver, peu fiable avec des cadres de
  // hauteurs très différentes et des cadres repliés.
  React.useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const offset = offsetRef.current;
      let current = '';
      for (const item of NAV_ITEMS) {
        const element = document.getElementById(item.id);
        if (element && element.getBoundingClientRect().top - offset <= 0) {
          current = item.id;
        }
      }
      setActiveId(current);
    };
    const onScroll = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  // La barre passe sur plusieurs lignes sur mobile : sa hauteur réelle sert de marge de défilement
  // aux cadres (--section-nav-offset) et de seuil pour la section courante.
  React.useEffect(() => {
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      const height = Math.ceil(nav.getBoundingClientRect().height);
      offsetRef.current = height + 24;
      document.documentElement.style.setProperty('--section-nav-offset', `${height + 16}px`);
    });
    observer.observe(nav);
    return () => observer.disconnect();
  }, []);

  return (
    <nav ref={navRef} className="section-nav" aria-label={t('editoTocTitle')}>
      <ul>
        {NAV_ITEMS.map((item) => (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              onClick={() => openCard(item.id)}
              className={activeId === item.id ? 'is-active' : undefined}
              aria-current={activeId === item.id ? 'location' : undefined}
            >
              {t(item.labelKey)}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
